// =============================================================================
// Zakładka Szablony — galeria katalogu wzorów wydruków. Katalog żyje W KODZIE
// (`packages/print-templates`, wzorzec email-templates), a DB trzyma wyłącznie
// odstępstwa tenanta — dlatego dane bierzemy z GET /api/print/templates
// (katalog + odstępstwa scalone po stronie print-core), a nie z tabeli.
// Bez wdrożonego print-core zakładka mówi to wprost, zamiast udawać katalog.
//
// Podgląd = POST /api/print/render, czyli PRAWDZIWA ścieżka renderu (plan
// §5.5) — pokazujemy dokładnie te bajty, które pojechałyby na drukarkę.
//
// DWA EDYTORY, JEDEN ZA FLAGĄ. „Projektuj" otwiera edytor WIZUALNY układów
// (E2) — to jest docelowa droga personalizacji i dlatego NIE ma flagi, tylko
// bramę roli (`ROLE_ADMINA_DRUKU`, lustro backendu). Stary edytor odstępstw
// (textarea Liquid + podgląd + zmienne) stoi ZA FLAGĄ
// `VITE_PRINT_TEMPLATE_EDITOR_ENABLED=true`; gdy endpoint upsertu nie
// odpowiada, degraduje do galerii tylko-do-odczytu.
//
// Brama roli MÓWI, że jest bramą (`printing.designNeedsAdmin`). Ukryty
// przycisk jest z ekranu nieodróżnialny od nieistniejącej funkcji — a to jest
// dokładnie ten wniosek, który wyciąga każdy, kto na tę zakładkę wejdzie bez
// roli.
// =============================================================================

import { Alert, Badge, Button, EmptyState, Modal, ModalFooter, Select } from '@insolitum/ui';
import { Eye, FileText, LayoutTemplate, Pencil, Printer, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '../../../providers/AuthProvider';

import { EdytorUkladu } from './edytor/EdytorUkladu';
import {
  komunikatLimituPlanu,
  PrintCoreError,
  pobierzSzablony,
  printCoreDostepny,
  renderujSzablon,
  usunOdstepstwo,
  utworzZadanie,
  zapiszOdstepstwo,
  type SzablonKatalogu,
  type WynikRenderu,
} from './printCoreApi';
import { ROLE_ADMINA_DRUKU } from './rolaAdmina';
import { usePrinters, type DrukarkaWiersz } from './usePrintingData';
import { zmienneWydrukuTestowego } from './zmienneWydruku';

/** Edytor odstępstw jest eksperymentalny — włącza go jawna flaga env. */
const EDYTOR_WLACZONY = import.meta.env.VITE_PRINT_TEMPLATE_EDITOR_ENABLED === 'true';

export function TemplatesTab() {
  const { t } = useTranslation();
  const { currentOrganization, hasRole } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const admin = hasRole([...ROLE_ADMINA_DRUKU]);
  const dostepny = printCoreDostepny();

  const [szablony, setSzablony] = useState<SzablonKatalogu[]>([]);
  const [loading, setLoading] = useState(dostepny);
  const [blad, setBlad] = useState<string | null>(null);
  const [sukces, setSukces] = useState<ReactNode | null>(null);
  const [podgladany, setPodgladany] = useState<SzablonKatalogu | null>(null);
  const [testowany, setTestowany] = useState<SzablonKatalogu | null>(null);
  const [edytowany, setEdytowany] = useState<SzablonKatalogu | null>(null);
  /** Edytor WIZUALNY (E2) — osobny od textarei Liquid i BEZ flagi: postać
   *  `layout` jest docelową drogą personalizacji, a nie eksperymentem. */
  const [projektowany, setProjektowany] = useState<SzablonKatalogu | null>(null);
  // Endpoint upsertu odstępstwa nie odpowiada → degradacja do read-only
  // (bez znikania galerii — psuje się tylko edycja).
  const [edytorNiedostepny, setEdytorNiedostepny] = useState(false);

  const { drukarki } = usePrinters();

  const wczytaj = useCallback(async () => {
    if (!orgId || !dostepny) return;
    setLoading(true);
    setBlad(null);
    try {
      setSzablony(await pobierzSzablony(orgId));
    } catch (err) {
      setBlad(
        err instanceof PrintCoreError ? err.message : 'Nie udało się wczytać katalogu szablonów'
      );
    } finally {
      setLoading(false);
    }
  }, [orgId, dostepny]);

  useEffect(() => {
    void wczytaj();
  }, [wczytaj]);

  const przywrocDomyslny = async (szablon: SzablonKatalogu) => {
    if (!orgId) return;
    if (
      !confirm(
        `Przywrócić domyślną wersję szablonu „${szablon.name}"? Odstępstwo organizacji zostanie usunięte.`
      )
    ) {
      return;
    }
    setBlad(null);
    try {
      await usunOdstepstwo(orgId, szablon.slug);
      setSukces(t('printing.overrideReset', 'Przywrócono domyślny szablon.'));
      void wczytaj();
    } catch (err) {
      setBlad(err instanceof PrintCoreError ? err.message : 'Nie udało się przywrócić szablonu');
    }
  };

  if (!dostepny) {
    return (
      <div className="space-y-4">
        <Alert variant="info">
          {t(
            'printing.templatesNoCore',
            'Katalog szablonów serwuje print-core, który nie jest jeszcze wdrożony. Pozostałe zakładki (odczyty kartotek) działają normalnie.'
          )}
        </Alert>
        <EmptyState
          variant="card"
          icon={<FileText className="w-8 h-8" />}
          title={t('printing.templatesSoon', 'Szablony pojawią się po wdrożeniu print-core')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {blad && (
        <Alert variant="error">
          {blad}{' '}
          <button onClick={() => void wczytaj()} className="underline">
            {t('printing.retry', 'Spróbuj ponownie')}
          </button>
        </Alert>
      )}
      {sukces && (
        <Alert variant="success" dismissible onDismiss={() => setSukces(null)}>
          {sukces}
        </Alert>
      )}
      {edytorNiedostepny && (
        <Alert variant="warning">
          {t(
            'printing.editorUnavailable',
            'Zapis odstępstw nie jest jeszcze dostępny po stronie print-core — galeria działa w trybie tylko do odczytu.'
          )}
        </Alert>
      )}
      {!admin && (
        <Alert variant="info">
          {t(
            'printing.designNeedsAdmin',
            'Projektowanie szablonów wymaga roli właściciela, administratora lub partnera w wybranej organizacji.'
          )}{' '}
          {t('printing.designYourRole', 'Twoja rola tutaj: {{rola}}.', {
            rola: currentOrganization?.role ?? '—',
          })}
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card p-4 h-40 animate-pulse" />
          ))}
        </div>
      ) : szablony.length === 0 ? (
        <EmptyState
          variant="card"
          icon={<FileText className="w-8 h-8" />}
          title={t('printing.noTemplates', 'Katalog szablonów jest pusty')}
          description={t(
            'printing.noTemplatesHint',
            'Wzory wydruków dochodzą do katalogu w kodzie (packages/print-templates) wraz z modułami.'
          )}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {szablony.map((szablon) => (
            <div key={szablon.slug} className="card p-4 flex flex-col gap-3">
              <div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-aurora-text">{szablon.name}</h3>
                  {szablon.customized && (
                    <Badge variant="warning" size="sm">
                      {t('printing.customized', 'dostosowany')}
                    </Badge>
                  )}
                </div>
                <p className="mono text-xs text-aurora-subtle mt-0.5">{szablon.slug}</p>
                {szablon.trigger && (
                  <p className="text-xs text-aurora-muted mt-1">{szablon.trigger}</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {szablon.organ && <span className="badge">{szablon.organ}</span>}
                {szablon.family && <span className="badge">{szablon.family}</span>}
                {(szablon.targets ?? []).map((cel) => (
                  <Badge key={cel} variant="info" size="sm">
                    {cel}
                  </Badge>
                ))}
              </div>
              <div className="mt-auto flex flex-wrap gap-2 pt-2 border-t border-aurora-border">
                <Button variant="secondary" size="sm" onClick={() => setPodgladany(szablon)}>
                  <Eye className="w-4 h-4" />
                  {t('printing.preview', 'Podgląd')}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setTestowany(szablon)}>
                  <Printer className="w-4 h-4" />
                  {t('printing.testPrint', 'Wydruk testowy')}
                </Button>
                {admin && (
                  <Button variant="primary" size="sm" onClick={() => setProjektowany(szablon)}>
                    <LayoutTemplate className="w-4 h-4" />
                    {t('printing.design', 'Projektuj')}
                  </Button>
                )}
                {EDYTOR_WLACZONY && admin && !edytorNiedostepny && (
                  <Button variant="secondary" size="sm" onClick={() => setEdytowany(szablon)}>
                    <Pencil className="w-4 h-4" />
                    {t('printing.edit', 'Edytuj Liquid')}
                  </Button>
                )}
                {admin && szablon.customized && (
                  <Button variant="ghost" size="sm" onClick={() => void przywrocDomyslny(szablon)}>
                    <RotateCcw className="w-4 h-4" />
                    {t('printing.resetDefault', 'Przywróć domyślny')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {podgladany && orgId && (
        <ModalPodgladu orgId={orgId} szablon={podgladany} onZamknij={() => setPodgladany(null)} />
      )}
      {testowany && orgId && (
        <ModalWydrukuTestowego
          orgId={orgId}
          szablon={testowany}
          drukarki={drukarki}
          onZamknij={() => setTestowany(null)}
          onZlecono={(tresc) => {
            setTestowany(null);
            setSukces(tresc);
          }}
        />
      )}
      {edytowany && orgId && (
        <ModalEdytora
          orgId={orgId}
          szablon={edytowany}
          onZamknij={() => setEdytowany(null)}
          onZapisano={() => {
            setEdytowany(null);
            setSukces(t('printing.overrideSaved', 'Odstępstwo zapisane.'));
            void wczytaj();
          }}
          onEndpointNiedostepny={() => {
            setEdytowany(null);
            setEdytorNiedostepny(true);
          }}
        />
      )}
      {projektowany && orgId && (
        <EdytorUkladu
          orgId={orgId}
          szablon={projektowany}
          onZamknij={() => setProjektowany(null)}
          onOpublikowano={() => {
            setProjektowany(null);
            setSukces(t('printing.layoutPublished', 'Układ opublikowany — wydruki idą z niego.'));
            void wczytaj();
          }}
        />
      )}
    </div>
  );
}

// ── Wspólny panel renderu (podgląd + prawa strona edytora) ───────────────────

function PanelRenderu({
  orgId,
  slug,
  cel,
  odswiezKlucz,
}: {
  orgId: string;
  slug: string;
  cel: string;
  /** Zmiana wartości wymusza ponowny render (np. po zapisie odstępstwa). */
  odswiezKlucz: number;
}) {
  const { t } = useTranslation();
  const [wynik, setWynik] = useState<WynikRenderu | null>(null);
  const [bladRenderu, setBladRenderu] = useState<string | null>(null);
  const [renderuje, setRenderuje] = useState(true);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let aktywny = true;
    setRenderuje(true);
    setBladRenderu(null);
    // Bez `variables`: podgląd idzie na danych PRZYKŁADOWYCH wzoru (`previewVars`
    // po stronie print-core). Jawne `{}` znaczyłoby „zero zmiennych" i kończyło
    // się 422 `missing_variable` dla każdego wzoru z polem nieopcjonalnym.
    // email-slug-dynamic: slug wzoru WYDRUKU (podgląd z katalogu print-templates), nie szablon e-mail
    void renderujSzablon(orgId, { template_slug: slug, target: cel })
      .then((odpowiedz) => {
        if (!aktywny) return;
        setWynik(odpowiedz);
      })
      .catch((err: unknown) => {
        if (!aktywny) return;
        setBladRenderu(err instanceof PrintCoreError ? err.message : 'Render nie powiódł się');
      })
      .finally(() => {
        if (aktywny) setRenderuje(false);
      });
    return () => {
      aktywny = false;
    };
  }, [orgId, slug, cel, odswiezKlucz]);

  // Blob → object URL, sprzątany przy każdej wymianie i przy odmontowaniu —
  // wyciek object URL-i to wyciek pamięci na długo otwartym panelu.
  useEffect(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    if (wynik?.blob) {
      urlRef.current = URL.createObjectURL(wynik.blob);
    }
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [wynik]);

  if (renderuje) {
    return <div className="h-72 card animate-pulse" />;
  }
  if (bladRenderu) {
    return <Alert variant="error">{bladRenderu}</Alert>;
  }
  if (!wynik) return null;

  const url = urlRef.current ?? wynik.url;
  if (url && (wynik.contentType.startsWith('application/pdf') || wynik.url)) {
    return (
      <iframe
        src={url}
        title={t('printing.previewFrame', 'Podgląd wydruku')}
        className="w-full h-72 rounded-ids-md border border-aurora-border bg-white"
      />
    );
  }
  if (url && wynik.contentType.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={t('printing.previewFrame', 'Podgląd wydruku')}
        className="max-w-full rounded-ids-md border border-aurora-border bg-white"
      />
    );
  }
  // Tekst (np. surowy ZPL) — karta ekspercka zamiast udawanego obrazka.
  return (
    <pre className="p-3 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md text-xs mono whitespace-pre-wrap break-all max-h-72 overflow-y-auto">
      {wynik.tekst ?? ''}
    </pre>
  );
}

// ── Modal podglądu ───────────────────────────────────────────────────────────

function ModalPodgladu({
  orgId,
  szablon,
  onZamknij,
}: {
  orgId: string;
  szablon: SzablonKatalogu;
  onZamknij: () => void;
}) {
  const { t } = useTranslation();
  const cele = szablon.targets ?? [];
  const [cel, setCel] = useState(cele[0] ?? 'PDF');

  return (
    <Modal open onClose={onZamknij} title={szablon.name} size="lg">
      <div className="space-y-4">
        {cele.length > 1 && (
          <Select
            label={t('printing.renderTarget', 'Cel renderu')}
            value={cel}
            onChange={(e) => setCel(e.target.value)}
            options={cele.map((c) => ({ value: c, label: c }))}
          />
        )}
        <PanelRenderu orgId={orgId} slug={szablon.slug} cel={cel} odswiezKlucz={0} />
        <p className="text-xs text-aurora-subtle">
          {t(
            'printing.previewNote',
            'Podgląd renderuje przykładowe dane z katalogu — tą samą ścieżką, którą pojedzie prawdziwy wydruk.'
          )}
        </p>
      </div>
    </Modal>
  );
}

// ── Modal wydruku testowego (per karta) ──────────────────────────────────────

function ModalWydrukuTestowego({
  orgId,
  szablon,
  drukarki,
  onZamknij,
  onZlecono,
}: {
  orgId: string;
  szablon: SzablonKatalogu;
  drukarki: DrukarkaWiersz[];
  onZamknij: () => void;
  onZlecono: (tresc: ReactNode) => void;
}) {
  const { t } = useTranslation();
  // Zgodność w miejscu wyboru: tylko drukarki mówiące jednym z celów wzoru
  // (drukarka `system` bez protokołu = PDF — jej kontrakt sterownikowy).
  const cele = szablon.targets ?? [];
  const zgodne = drukarki.filter((d) => {
    if (!d.is_active) return false;
    const protokol = d.protocol ?? (d.type === 'system' ? 'PDF' : null);
    return protokol !== null && cele.includes(protokol);
  });
  const [drukarkaId, setDrukarkaId] = useState(zgodne[0]?.id ?? '');
  const [zleca, setZleca] = useState(false);
  const [bladZlecenia, setBladZlecenia] = useState<string | null>(null);

  const zlec = async () => {
    const drukarka = zgodne.find((d) => d.id === drukarkaId);
    if (!drukarka) return;
    setZleca(true);
    setBladZlecenia(null);
    try {
      const zadanie = await utworzZadanie(orgId, {
        // email-slug-dynamic: slug wzoru WYDRUKU z galerii katalogu print-templates, nie szablon e-mail
        template_slug: szablon.slug,
        // Przykłady z deklaracji wzoru + żywe `printed_at`/`printer_name` tam,
        // gdzie wzór je deklaruje. Nigdy `{}` — bramka pustego payloadu odbija.
        variables: zmienneWydrukuTestowego(szablon.variables ?? [], { drukarka }),
        printer_id: drukarkaId,
        copies: 1,
        client_job_key: crypto.randomUUID(),
      });
      onZlecono(
        <span>
          {t('printing.testEnqueued', 'Wydruk testowy zlecony.')}{' '}
          <Link to={`/admin/printing?tab=jobs&job=${zadanie.id}`} className="underline font-medium">
            {t('printing.seeInQueue', 'Zobacz w kolejce')}
          </Link>
        </span>
      );
    } catch (err) {
      setBladZlecenia(
        err instanceof PrintCoreError ? err.message : 'Nie udało się zlecić wydruku testowego'
      );
      setZleca(false);
    }
  };

  return (
    <Modal
      open
      onClose={onZamknij}
      title={t('printing.testPrintTitle', 'Wydruk testowy: {{name}}', { name: szablon.name })}
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onZamknij}>
            {t('printing.cancel', 'Anuluj')}
          </Button>
          <Button onClick={() => void zlec()} loading={zleca} disabled={!drukarkaId}>
            <Printer className="w-4 h-4" />
            {t('printing.print', 'Drukuj')}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {bladZlecenia && <Alert variant="error">{bladZlecenia}</Alert>}
        {zgodne.length === 0 ? (
          <Alert variant="warning">
            {t(
              'printing.noCompatiblePrinters',
              'Brak aktywnej drukarki zgodnej z celami tego wzoru ({{targets}}).',
              { targets: cele.join(', ') || '—' }
            )}
          </Alert>
        ) : (
          <Select
            label={t('printing.colPrinter', 'Drukarka')}
            value={drukarkaId}
            onChange={(e) => setDrukarkaId(e.target.value)}
            options={zgodne.map((d) => ({
              value: d.id,
              label: `${d.name} (${d.protocol ?? 'PDF'})`,
            }))}
            helperText={t(
              'printing.compatibleOnly',
              'Lista zawiera wyłącznie drukarki zgodne z formatami wzoru.'
            )}
          />
        )}
      </div>
    </Modal>
  );
}

// ── Modal edytora odstępstwa (za flagą) ──────────────────────────────────────

function ModalEdytora({
  orgId,
  szablon,
  onZamknij,
  onZapisano,
  onEndpointNiedostepny,
}: {
  orgId: string;
  szablon: SzablonKatalogu;
  onZamknij: () => void;
  onZapisano: () => void;
  onEndpointNiedostepny: () => void;
}) {
  const { t } = useTranslation();
  const [liquid, setLiquid] = useState(szablon.override_liquid ?? '');
  const [zapisuje, setZapisuje] = useState(false);
  const [bladZapisu, setBladZapisu] = useState<string | null>(null);
  const [brudny, setBrudny] = useState(false);
  const [odswiezKlucz, setOdswiezKlucz] = useState(0);
  const cel = (szablon.targets ?? [])[0] ?? 'PDF';

  const zamknij = () => {
    // Strażnik brudnego zamknięcia — wzorzec labels z PackFlow, jedna dobra rzecz.
    if (brudny && !confirm('Masz niezapisane zmiany. Zamknąć bez zapisu?')) return;
    onZamknij();
  };

  const zapisz = async () => {
    setZapisuje(true);
    setBladZapisu(null);
    try {
      await zapiszOdstepstwo(orgId, szablon.slug, { liquid });
      setBrudny(false);
      setOdswiezKlucz((k) => k + 1);
      onZapisano();
    } catch (err) {
      if (err instanceof PrintCoreError && (err.status === 404 || err.status === 405)) {
        // Endpoint jeszcze nie istnieje — degradacja całej zakładki do read-only.
        onEndpointNiedostepny();
        return;
      }
      setBladZapisu(
        komunikatLimituPlanu(err) ??
          (err instanceof PrintCoreError ? err.message : 'Zapis nie powiódł się')
      );
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <Modal
      open
      onClose={zamknij}
      title={t('printing.editorTitle', 'Odstępstwo: {{name}}', { name: szablon.name })}
      size="xl"
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={zamknij}>
            {t('printing.cancel', 'Anuluj')}
          </Button>
          <Button onClick={() => void zapisz()} loading={zapisuje}>
            {t('printing.save', 'Zapisz zmiany')}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {bladZapisu && <Alert variant="error">{bladZapisu}</Alert>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="field__label">Liquid</label>
            <textarea
              value={liquid}
              onChange={(e) => {
                setLiquid(e.target.value);
                setBrudny(true);
              }}
              rows={16}
              spellCheck={false}
              className="textarea mono text-xs w-full"
              placeholder={t(
                'printing.editorPlaceholder',
                'Pusty edytor = szablon domyślny z katalogu. Wpisz Liquid, aby nadpisać.'
              )}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="field__label">{t('printing.preview', 'Podgląd')}</label>
              <span className="text-xs text-aurora-subtle">
                {t('printing.previewSavedState', 'podgląd pokazuje ZAPISANY stan')}
              </span>
            </div>
            <PanelRenderu orgId={orgId} slug={szablon.slug} cel={cel} odswiezKlucz={odswiezKlucz} />
          </div>
        </div>
        {(szablon.variables ?? []).length > 0 && (
          <div>
            <h3 className="overline mb-2">{t('printing.variables', 'Zmienne wzoru')}</h3>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {(szablon.variables ?? []).map((zmienna) => (
                <div key={zmienna.name} className="text-xs flex flex-wrap gap-2">
                  <span className="mono text-aurora-text">
                    {'{{'}
                    {zmienna.name}
                    {'}}'}
                  </span>
                  <span className="text-aurora-muted">{zmienna.description ?? ''}</span>
                  {zmienna.example !== undefined && (
                    <span className="text-aurora-subtle mono">np. {String(zmienna.example)}</span>
                  )}
                  {zmienna.optional && <span className="text-aurora-subtle">(opcjonalna)</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
